import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Star, Package, User } from 'lucide-react';
import api from '../../api/axios';

const AdminReviewsPage = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchReviews();
    }, []);

    const fetchReviews = async () => {
        try {
            // Llama a la ruta protegida que acabamos de crear
            const response = await api.get('/admin/reviews');
            setReviews(response.data);
        } catch (error) {
            console.error("Error al cargar las reseñas de admin:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleVisibility = async (id) => {
        try {
            // Hace la petición PATCH a Laravel
            const response = await api.patch(`/admin/reviews/${id}/toggle-visibility`);
            
            // Actualiza la tabla en tiempo real sin tener que recargar la página
            setReviews(reviews.map(review => 
                review.id === id 
                    ? { ...review, is_visible: response.data.review.is_visible } 
                    : review
            ));
        } catch (error) {
            console.error("Error al cambiar la visibilidad:", error);
            alert("Error al actualizar. Verifica que tengas permisos de administrador.");
        }
    };

    if (loading) {
        return <div className="p-10 text-center font-bold text-[#CE010B] animate-pulse">Cargando panel de moderación...</div>;
    }

    return (
        <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-3xl font-black uppercase text-black dark:text-white">Moderación de <span className="text-[#CE010B]">Reseñas</span></h2>
                    <p className="text-gray-500 dark:text-white/60 font-bold text-sm mt-1">Gestiona qué opiniones se muestran en la página pública.</p>
                </div>
            </div>

            {/* TABLA DE RESEÑAS */}
            <div className="bg-white dark:bg-black/20 border border-black/10 dark:border-white/10 rounded-3xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-black/5 dark:bg-white/5 border-b border-black/10 dark:border-white/10 text-xs uppercase tracking-widest text-gray-500 dark:text-white/60">
                                <th className="p-5 font-black">Socio / Envío</th>
                                <th className="p-5 font-black">Calificación</th>
                                <th className="p-5 font-black">Comentario</th>
                                <th className="p-5 font-black">Fecha</th>
                                <th className="p-5 font-black text-center">Estado / Acción</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5 dark:divide-white/5 text-sm">
                            {reviews.length > 0 ? reviews.map((review) => (
                                <tr key={review.id} className="hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors">
                                    {/* SOCIO Y GUÍA */}
                                    <td className="p-5">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-black/5 dark:bg-white/10 rounded-full text-black dark:text-white">
                                                <User size={16} />
                                            </div>
                                            <div>
                                                <p className="font-bold text-black dark:text-white">{review.user?.name}</p>
                                                <p className="text-[10px] text-gray-500 flex items-center gap-1 font-black uppercase tracking-widest">
                                                    <Package size={10} /> {review.shipment?.tracking_number}
                                                </p>
                                            </div>
                                        </div>
                                    </td>

                                    {/* ESTRELLAS */}
                                    <td className="p-5">
                                        <div className="flex gap-0.5">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <Star 
                                                    key={star} 
                                                    size={14} 
                                                    className={star <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-gray-600'} 
                                                />
                                            ))}
                                        </div>
                                    </td>

                                    {/* COMENTARIO */}
                                    <td className="p-5 max-w-xs">
                                        <p className="text-gray-600 dark:text-white/80 italic truncate" title={review.comment}>
                                            "{review.comment}"
                                        </p>
                                    </td>

                                    {/* FECHA */}
                                    <td className="p-5 text-gray-500 dark:text-white/60 text-xs font-bold uppercase tracking-wider">
                                        {new Date(review.created_at).toLocaleDateString('es-MX')}
                                    </td>

                                    {/* BOTÓN DE TOGGLE */}
                                    <td className="p-5 text-center">
                                        <button
                                            onClick={() => handleToggleVisibility(review.id)}
                                            className={`flex items-center justify-center gap-2 px-4 py-2 mx-auto rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-sm ${
                                                review.is_visible 
                                                    ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                                                    : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                                            }`}
                                        >
                                            {review.is_visible ? (
                                                <><Eye size={16} /> Visible</>
                                            ) : (
                                                <><EyeOff size={16} /> Oculta</>
                                            )}
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="p-10 text-center text-gray-500 font-bold uppercase tracking-widest">
                                        No hay reseñas registradas
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminReviewsPage;